import { useEffect, useLayoutEffect } from 'react';
import { useParams, useLocation, useNavigationType } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getCategoryPageData } from '../api/cmsApi';

import TextHeader from '../../../components/textHeader/TextHeader';
import FilterComponent from '../../../components/shared/filter/FilterComponent';
import LoadingScreen from '../../../components/shared/loading/LoadingScreen';
import MissingPage from '../../../components/shared/errorMessages/MissingPage';
import { capitalizeString } from '../../../utilities/TextManipulation';
import { captureAnalytics } from '../../../utilities/Analytics';
import { CMSPageData } from '../types/CMSTypes';

// Import new CMS components
import CMSConsumerCarouselBanner from '../components/banner/CMSConsumerCarouselBanner';
import CMSConsumerFeaturedGallery from '../components/gallery/CMSConsumerFeaturedGallery';
import CMSConsumerNewArrivalsGrid from '../components/grid/CMSConsumerNewArrivalsGrid';
import CMSConsumerCollectionsGrid from '../components/grid/CMSConsumerCollectionsGrid';

function ApplicationsTemplate() {
  const { item_group, application } = useParams();
  const location = useLocation();
  const navigationType = useNavigationType();

  // CMS Data Query - Consumer always uses live mode, searches by application name
  const {
    isSuccess: cmsSuccess,
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse,
    refetch: cmsRefetch
  } = useQuery(
    [`cms-consumer-application-${application}`],
    () => getCategoryPageData(application || '', 'live'), // Always live mode for consumer
    {
      staleTime: 600000, // Cache data for 10 minutes
      enabled: !!application, // Only run if application exists
    }
  );

  // Get the CMS data (always use live data for consumer)
  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  // Get the collections data - always use active data for consumer
  const collectionsData = cmsData?.direct_collections?.active || [];

  // Get the new arrivals data and filter published items only
  const rawNewArrivalsData = cmsData?.new_arrivals?.active || [];
  const newArrivalsData = rawNewArrivalsData
    .filter(item => item.is_published) // Consumer only sees published items
    .sort((a, b) => {
      const orderA = a.order_position || 999;
      const orderB = b.order_position || 999;
      return orderA - orderB;
    });

  // Complex scroll management for filter interactions (keeping original logic)
  useEffect(() => {
    // Get multifilter status first
    const multifilterActive = sessionStorage.getItem('multifilter_active') === 'true';
    const multifilterPosition = multifilterActive ? sessionStorage.getItem('multifilter_scroll_position') : null;
    const changingFromCategory = sessionStorage.getItem('changing_from_category_to_application') === 'true';
    const fromFilterProduct = sessionStorage.getItem('fromFilterComponent') === 'true';

    // Returning from a PDP: FilterComponent restores the exact scroll position
    // (and re-loads enough items in "Show All"), so don't interfere here.
    if (navigationType === 'POP' && sessionStorage.getItem('multifilter_restore')) {
      return;
    }

    // For back/forward navigation
    if (navigationType === 'POP') {
      // If we're coming back from a product page to an application page
      if (fromFilterProduct &&
        (location.pathname.includes('/application/') || location.pathname.includes('/collection/'))) {
        // Use requestAnimationFrame for more reliable DOM access
        requestAnimationFrame(() => {
          // First try to restore multifilter position if available
          if (multifilterActive && multifilterPosition) {
            window.scrollTo({
              top: parseInt(multifilterPosition, 10),
              behavior: 'auto' // Use auto for immediate scroll
            });
          } else {
            // Fall back to filter component scrolling
            const filterElement = document.getElementById('filter-component-section');
            if (filterElement) {
              filterElement.scrollIntoView({ behavior: 'auto', block: 'start' });
            } else {
              // Last resort - scroll to top
              window.scrollTo(0, 0);
            }
          }

          // Clear the product navigation flag but keep multifilter flags
          sessionStorage.removeItem('fromFilterComponent');
        });
      }
      // If we have a saved multifilter position but aren't coming from a product
      else if (multifilterActive && multifilterPosition) {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: parseInt(multifilterPosition, 10),
            behavior: 'auto'
          });
        });
      }
      // For all other back navigation (including when cache is cleared), let browser handle naturally
    } else {
      // This is a fresh navigation (PUSH/REPLACE)

      // If we just switched from category to application via multifilter
      if (changingFromCategory) {
        // Clear the flag but don't scroll - the filter component handles this
        sessionStorage.removeItem('changing_from_category_to_application');
        return;
      }

      // Don't scroll if we're on a page with filter params
      if (location.search.includes('page=') ||
        location.search.includes('colours=') ||
        location.search.includes('types=') ||
        location.search.includes('design_style=') ||
        location.search.includes('brand=')) {
        return;
      }

      // For all other cases, scroll to top
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.search, navigationType]);

  // Add cleanup effect to clean up multifilter flags when leaving the application page
  useEffect(() => {
    return () => {
      // If we're navigating away from application page but NOT to a product page
      // then clear multifilter flags
      if (!location.pathname.includes('/design/')) {
        sessionStorage.removeItem('multifilter_active');
        sessionStorage.removeItem('multifilter_scroll_position');
        sessionStorage.removeItem('multifilter_item_group');
      }
    };
  }, [location.pathname]);

  // Analytics tracking
  useEffect(() => {
    if (cmsSuccess && cmsData && application) {
      const title = cmsData.page_title || `application-${application}`;
      captureAnalytics('pageview', location.pathname, title);
    }
  }, [location.pathname, application, cmsSuccess, cmsData]);

  // Determine loading and error states
  const isLoading = cmsLoading;
  const isError = cmsError;
  const isSuccess = cmsSuccess;

  if (!application) {
    return <MissingPage />;
  }

  return (
    <>
      {isLoading && <LoadingScreen />}
      {isError && <MissingPage />}
      {isSuccess && (
        <>
          {/* CMS Consumer Carousel Banner */}
          {cmsData && (
            <CMSConsumerCarouselBanner
              bannerMediaOption={cmsData.consumer_banner_media_option}
              bannerMediaCarousel={cmsData.consumer_banner_media_carousel || []}
              bannerMediaVideo={cmsData.consumer_banner_media_video}
              bannerMediaVideoLink={cmsData.consumer_banner_media_video_link}
              bannerLinkCategoryId={cmsData.consumer_banner_link_category_id}
              bannerWidth={cmsData.consumer_banner_width}
              bannerHeight={cmsData.consumer_banner_height}
              viewMode="live"
            />
          )}

          {/* Text Header */}
          <TextHeader
            heading={cmsData?.page_title || capitalizeString(application)}
            content={cmsData?.description || ''}
            isCentered={false}
            width='70%'
            marginTop={10}
            marginBottom={5}
          />

          {/* CMS Consumer Featured Gallery - Only show if enabled and has data */}
          {cmsData?.featured_collection_display_switch && cmsData?.featured_collection && (
            <CMSConsumerFeaturedGallery
              featuredCollection={cmsData.featured_collection}
              hasVideo={false}
              invert={false}
              marginTop={5}
              marginBottom={5}
              viewMode="live"
            />
          )}

          {/* CMS Consumer Collections Grid - Show collections for this application */}
          {collectionsData.length > 0 && (
            <CMSConsumerCollectionsGrid
              collectionsData={collectionsData}
              parentCategoryId={(cmsResponse?.main_cms_category ?? cmsResponse?.cms_category)?.id}
              applicationName={application}
              marginTop={5}
              marginBottom={5}
              viewMode="live"
            />
          )}

          {/* Filter Component Section */}
          <div id="filter-component-section">
            <FilterComponent
              itemGroup={item_group}
              title={capitalizeString(application)}
              navigationType={navigationType}
            />
          </div>

          {/* CMS Consumer New Arrivals - Only show if has data */}
          {newArrivalsData.length > 0 && (
            <CMSConsumerNewArrivalsGrid
              newArrivalsData={newArrivalsData}
              header="New arrivals"
              hasTitles={true}
              hasButtons={true}
              hasBackgroundText={false}
              spacingValue={4}
              marginTop={'5rem'}
              marginBottom={'5rem'}
              viewMode="live"
            />
          )}
        </>
      )}
    </>
  );
}

export default ApplicationsTemplate;